import { useEffect, useRef, useState, type CSSProperties } from "react"
import { createPortal } from "react-dom"
import styled from "styled-components"
import { systemPost } from "src/services/superAdminApi"
import { cardDisplayStatus } from "src/utils/cardStatus"
import { T } from "../adminTheme"
import { Avatar } from "./adminUi"

type Kind = "user" | "company" | "holder" | "card"
interface Option { uid: string; name: string; detail: string }
interface Row {
	uid: string; name?: string; email?: string; company_name?: string; admins?: Array<{ email?: string }>
	user_name?: string; user_email?: string; provider_code?: string; type?: string; mask?: string; last4?: string; status?: string; blocking?: boolean
}

const rows = (data: unknown): Row[] => Array.isArray(data) ? data : Array.isArray((data as { rows?: Row[] })?.rows) ? (data as { rows: Row[] }).rows : []

// ponytail: companies are fetched once per session and filtered locally; add a server search if the list grows past a few thousand.
let companyCache: Promise<Row[]> | null = null
const loadCompanies = (token: string) => companyCache ??= systemPost<unknown>(token, "system/company/list", { limit: 1000, with_meta: true })
	.then((response) => rows(response.data))
	.catch(() => { companyCache = null; return [] })

const search = async (token: string, kind: Kind, query: string, companies?: Row[]): Promise<Option[]> => {
	const needle = query.trim().toLowerCase()
	if (kind === "holder") {
		const response = await systemPost<unknown>(token, "system/cardholders/list", { search: query.trim(), limit: 8 })
		return rows(response.data).map((holder) => ({ uid: holder.uid, name: holder.user_name || holder.user_email || holder.uid, detail: [holder.user_email, holder.provider_code, holder.type].filter(Boolean).join(" · ") }))
	}
	if (kind === "card") {
		const response = await systemPost<unknown>(token, "system/cards/list", { search: query.trim(), limit: 8, with_meta: true })
		return rows(response.data).map((card) => ({ uid: card.uid, name: card.mask || (card.last4 ? `•• ${card.last4}` : card.name || card.uid), detail: [card.user_name || card.name, cardDisplayStatus(card)].filter(Boolean).join(" · ") }))
	}
	if (kind === "user") {
		const response = await systemPost<unknown>(token, "system/user/list", { search: query.trim(), limit: 8 })
		return rows(response.data).map((user) => ({ uid: user.uid, name: user.name || user.email || user.uid, detail: [user.email, user.company_name !== user.name && user.company_name].filter(Boolean).join(" · ") }))
	}
	return (companies ?? await loadCompanies(token))
		.filter((company) => [company.uid, company.name, company.admins?.[0]?.email].some((value) => value?.toLowerCase().includes(needle)))
		.slice(0, 8)
		.map((company) => ({ uid: company.uid, name: company.name || company.uid, detail: company.admins?.[0]?.email ?? "" }))
}

const PLACEHOLDER: Record<Kind, string> = {
	user: "Name, e-mail or UID", company: "Company name, e-mail or UID", holder: "Holder name, e-mail or UID", card: "Card number, owner or UID"
}

const List = styled.div`
	position: fixed; z-index: 10050; display: grid; padding: 5px; max-height: 320px; overflow-y: auto; background: ${T.surface}; color: ${T.text};
	border: 1px solid ${T.border}; border-radius: 10px; box-shadow: ${T.shadowLg}; animation: bo-pop .16s ${T.ease} both; font-size: 13.5px;
	button { display: flex; gap: 10px; align-items: center; text-align: left; border: 0; background: none; padding: 7px 9px; border-radius: 7px; font: inherit; cursor: pointer; color: inherit; }
	button[aria-selected="true"], button:hover { background: ${T.hover}; }
	small { display: block; color: ${T.muted}; font-size: 12.5px; }
	code { color: ${T.muted}; font-size: 12px; }
	p { margin: 0; padding: 8px 10px; color: ${T.muted}; }
`

/**
 * UID field that also searches: paste a UID, or type a name / e-mail and pick from the list.
 * The value is always the UID, so callers keep sending exactly what they sent before.
 */
export const EntityInput = ({ token, kind, value, onChange, placeholder, disabled, name, style, companies }: {
	token: string; kind: Kind; value: string; onChange: (uid: string) => void; placeholder?: string; disabled?: boolean; name?: string; style?: CSSProperties
	/** Already-loaded companies; skips the extra list request. */
	companies?: Row[]
}) => {
	const [text, setText] = useState(value)
	const [options, setOptions] = useState<Option[] | null>(null)
	const [active, setActive] = useState(0)
	const [picked, setPicked] = useState<Option | null>(null)
	const [place, setPlace] = useState<CSSProperties | null>(null)
	const input = useRef<HTMLInputElement>(null)
	const request = useRef(0)

	// Keep the box in sync when the parent resets or pre-fills the value.
	useEffect(() => { if (value !== (picked?.uid ?? text.trim())) { setText(value); setPicked(null) } }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const query = text.trim()
		if (!place || query.length < 2 || picked) { setOptions(null); return }
		const id = ++request.current
		const timer = window.setTimeout(() => void search(token, kind, query, companies).then((found) => { if (id === request.current) { setOptions(found); setActive(0) } }), 250)
		return () => window.clearTimeout(timer)
	}, [text, place, picked, token, kind, companies])

	const open = () => {
		const rect = input.current?.getBoundingClientRect()
		if (rect) setPlace({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 300) })
	}
	const choose = (option: Option) => { setPicked(option); setText(option.uid); setPlace(null); onChange(option.uid) }

	return <span style={{ position: "relative", display: "block" }}>
		<input ref={input} name={name} value={text} disabled={disabled} autoComplete='off' style={picked ? { ...style, paddingRight: "62%" } : style}
			placeholder={placeholder ?? PLACEHOLDER[kind]}
			role='combobox' aria-expanded={Boolean(options?.length)} aria-autocomplete='list'
			onFocus={open} onBlur={() => { if (document.hasFocus()) window.setTimeout(() => setPlace(null), 150) }}
			onChange={(event) => { setText(event.target.value); setPicked(null); onChange(event.target.value.trim()); open() }}
			onKeyDown={(event) => {
				if (!options?.length) return
				if (event.key === "ArrowDown") { event.preventDefault(); setActive((index) => Math.min(index + 1, options.length - 1)) }
				if (event.key === "ArrowUp") { event.preventDefault(); setActive((index) => Math.max(index - 1, 0)) }
				if (event.key === "Enter" && options[active]) { event.preventDefault(); event.stopPropagation(); choose(options[active]) }
				if (event.key === "Escape") setPlace(null)
			}} />
		{/* The chosen person sits inside the field, so picking never shifts the form layout. */}
		{picked && <span title={[picked.name, picked.detail].filter(Boolean).join(" · ")} style={{ position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)", maxWidth: "60%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: T.muted, fontSize: 13, fontWeight: 400, pointerEvents: "none" }}>{picked.name}</span>}
		{place && options && createPortal(<List role='listbox' style={place} onMouseDown={(event) => event.preventDefault()}>
			{options.length ? options.map((option, index) => <button key={option.uid} type='button' role='option' aria-selected={index === active} onMouseEnter={() => setActive(index)} onClick={() => choose(option)}>
				<Avatar name={option.name} size={26} />
				<span style={{ minWidth: 0, flex: 1 }}><span style={{ fontWeight: 550 }}>{option.name}</span><small>{option.detail || " "}</small></span>
				<code>{option.uid}</code>
			</button>) : <p>Nothing found. You can still paste an exact UID.</p>}
		</List>, document.body)}
	</span>
}
